import { autoElement } from '../../../webmodule-common/components/src/webmodule-components';
import { currentUserClaims } from '../../../webmodule-common/other/currentuser-claims';
import { getCredentials } from '../../api/licenseserver-credentials';
import { html } from 'lit';

import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { PromiseTemplate, Snippet } from '../../../webmodule-common/interop/webmodule-interop';
import { tlang } from '../../../webmodule-common/other/language/lang';
import {
  ILicenseServerUIStaffPageProvider,
  IStaffPage,
  licenseServerUIBootLoader,
  ViewBaseLogin
} from '../../common/licenseserver-integration';
import {
  BaseLoginModalEditProperties,
  ILoginClaimManager
} from '../../../scripts/franchisee/data/licenseserver-integration';
import { claimIdentity } from '../../../webmodule-common/other/api/user-security';
import { isAdmin, isSupportManager } from '../../common/user-permissions';

@autoElement()
export class SettingsStaffView extends PageControlTabWithIndependantSaving {
  private staffPageProvider: ILicenseServerUIStaffPageProvider;
  private userConsole?: IStaffPage;

  constructor() {
    super();
    this.pageFragment = 'staff';

    this.staffPageProvider = this.staffPageProviderFactory();

    console.log(JSON.stringify(this.staffPageProvider.getColumnProperties?.()));
  }

  public async afterConstruction(): Promise<void> {
    const licUI = await licenseServerUIBootLoader();

    this.userConsole = licUI?.getStaffPage(this.staffPageProvider);
  }

  get isAdmin(): boolean {
    return isAdmin();
  }

  get isSupportManager(): boolean {
    return isSupportManager();
  }

  buildModalProperties(): BaseLoginModalEditProperties[] {
    const results: BaseLoginModalEditProperties[] = [];
    results.push(this.buildSupportManagerProperty());
    results.push(this.buildAllTenantAccessProperty());
    return results;
  }

  buildSupportManagerProperty(): BaseLoginModalEditProperties {
    let editorValue: boolean | undefined = undefined;
    const changeEvent = (e: Event) => (editorValue = (e.currentTarget as HTMLSelectElement).value === 'true');

    const editor: BaseLoginModalEditProperties = {
      inputId: 'isSupportManager',
      visible: true,
      editControl: (claimManager: ILoginClaimManager) => {
        const checked = claimManager.hasTrueClaim(claimIdentity.supportManager);
        return html`<select
          ?disabled=${!this.isSupportManager}
          @change=${changeEvent}
          id="isSupportManager"
          class="form-select"
        >
          <option ?selected=${!checked} value="">Is not a Support Manager</option>
          <option ?selected=${checked} value="true">Is a Support Manager</option>
        </select>`;
      },
      label: `Tenant Support Manager`,
      propertyId: 1,
      save: (claimManager: ILoginClaimManager) => {
        if (this.isSupportManager) claimManager.setTrueClaim(claimIdentity.supportManager, editorValue ?? false);
      }
    };
    return editor;
  }

  buildAllTenantAccessProperty(): BaseLoginModalEditProperties {
    let editorValue: boolean | undefined = undefined;
    const changeEvent = (e: Event) => (editorValue = (e.currentTarget as HTMLSelectElement).value === 'true');

    const editor: BaseLoginModalEditProperties = {
      inputId: 'allTenantAccess',
      visible: true,
      editControl: (claimManager: ILoginClaimManager) => {
        const checked = claimManager.hasTrueClaim(claimIdentity.supportTenantAllAccess);
        return html`<select
          ?disabled=${!this.isSupportManager}
          @change=${changeEvent}
          id="allTenantAccess"
          class="form-select"
        >
          <option ?selected=${!checked} value="">Has limited tenant support access</option>
          <option ?selected=${checked} value="true">Has full tenant support access</option>
        </select>`;
      },
      label: `Tenant Support Access`,
      propertyId: 2,
      save: (claimManager: ILoginClaimManager) => {
        if (this.isSupportManager)
          claimManager.setTrueClaim(claimIdentity.supportTenantAllAccess, editorValue ?? false);
      }
    };
    return editor;
  }

  public allowDeletePage(): boolean {
    return false;
  }

  async prepareForSave(): Promise<void> {
    //
  }

  public internalDataChanged(): boolean {
    return false;
  }

  async onEnter(): Promise<void> {
    this.requestUpdate();
  }

  public buttonMenu(): Snippet {
    const isAdmin = currentUserClaims().isAdmin;
    const buttons = this.userConsole?.actions().map(x => {
      return html` <button class="btn btn-primary" ?disabled=${!x.enabled || !isAdmin} @click=${x.event}>
        ${x.label}
      </button>`;
    });
    return html`${buttons}`;
  }

  protected getCaption(): Snippet {
    return tlang`Staff`;
  }

  protected async internalSaveData(): Promise<boolean> {
    //nothing to do
    return true;
  }

  protected async bodyTemplate(): PromiseTemplate {
    return this.userConsole?.template() ?? html``;
  }

  protected staffPageProviderFactory(): ILicenseServerUIStaffPageProvider {
    return {
      credentials: getCredentials(),
      onAfterSave: async (login: ViewBaseLogin) => {
        console.log(`login saved ${login.baseLogin.userName}`);
      },
      onBeforeSave: async (login: ViewBaseLogin) => {
        console.log(`login saving ${login.baseLogin.userName}`);
        return true;
      },
      getModalProperties: () => this.buildModalProperties(),
      getColumnProperties: () => {
        return [
          {
            displayValue: (login: ViewBaseLogin) => {
              const allStr =
                login.deploymentClaims[claimIdentity.supportManager] === 'true' ||
                login.baseLogin.claims[claimIdentity.licenseServerAdmin] === 'true'
                  ? 'Yes'
                  : 'No';
              return html`<span>${allStr}</span>`;
            },
            title: 'Support Manager'
          },
          {
            displayValue: (login: ViewBaseLogin) => {
              const allStr =
                login.deploymentClaims[claimIdentity.supportTenantAllAccess] === 'true' ||
                login.deploymentClaims[claimIdentity.supportManager] === 'true' ||
                login.baseLogin.claims[claimIdentity.licenseServerAdmin] === 'true'
                  ? 'Full'
                  : 'Limited';
              return html`<span>${allStr}</span>`;
            },
            title: 'Tenant Access'
          }
        ];
      },
      canEditUser: (_viewLogin: ViewBaseLogin): boolean => {
        //full admin can edit anyone
        const claims = currentUserClaims();
        if (claims.isAdmin || claims.isSuperAdmin || claims.isLicenseServerAdmin) return true;
        //if admin, and only admin, we can see everything.
        return false;
      },

      uiChanged: () => this.requestUpdate()
    };
  }
}
